import { get, isEmpty, orderBy, partition } from 'lodash';
import { PLAYS_GRID_COLUMNS } from './PlaysGrid.constants';
import { SORT_DIRECTIONS } from '../../../utils/constants/sortDirections';
import { millisecondsToTime } from '../../../utils/helpers/time';
import { TABLE_NULL } from '../../../utils/helpers/formatting';
import {
  getDownName,
  getYardlineName,
  getYardsWon,
} from '../../../utils/helpers/play';
import { formatValue } from '../../../utils/helpers/stats.dataManipulation';
import { API_PLAY_TYPES, API_STAT_UNITS } from '../../../utils/constants/api';

const isBlank = (v) => v === null || v === undefined || v === '';

export const formatPlain = (v) => (isBlank(v) ? TABLE_NULL : String(v));

// Reuse the shared clock formatter (same mm:ss used by PlayFinder etc.).
export const formatClock = (ms) =>
  isBlank(ms) ? TABLE_NULL : millisecondsToTime(ms);

export const formatEnum = (raw) => {
  if (isBlank(raw) || typeof raw !== 'string') return TABLE_NULL;
  return raw
    .replace(/^[A-Z][a-zA-Z]+\./, '')
    .toLowerCase()
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');
};

// Reuse the shared value formatter for booleans (Yes / No / '-').
export const formatBool = (v) => formatValue(v, API_STAT_UNITS.BOOLEAN);

export const formatOffPers = (p) =>
  p && typeof p === 'object'
    ? `${p.RB ?? 0}-${p.TE ?? 0}-${p.WR ?? 0}-${p.OL ?? 0}-${p.QB ?? 0}`
    : TABLE_NULL;

export const formatDefPers = (p) =>
  p && typeof p === 'object'
    ? `${p.DL ?? 0}-${p.LB ?? 0}-${p.DB ?? 0}`
    : TABLE_NULL;

export const formatPenalties = (list) =>
  Array.isArray(list) && list.length
    ? list.map((pen) => pen.name || pen.type).join('; ')
    : TABLE_NULL;

export const formatTeam = (t) =>
  t ? t.shortName || t.name || TABLE_NULL : TABLE_NULL;

export const formatDecimal = (v) =>
  typeof v === 'number' ? v.toFixed(2) : TABLE_NULL;

// Resolve the display label for one column of one play. Down, yard line,
// play type and net yards reuse the shared play helpers PlayFinder uses so
// labels read the same across the app (e.g. "1st Down", "Own 25", "Run").
const cellLabel = (play, column) => {
  switch (column.format) {
    case 'clock':
      return formatClock(play.quarterClockRemaining);
    case 'down':
      return play.down == null ? TABLE_NULL : getDownName(play.down);
    case 'yardLine':
      return getYardlineName(play.yardLine) || TABLE_NULL;
    case 'playType':
      return API_PLAY_TYPES[play.type] || TABLE_NULL;
    case 'yardsWon':
      return getYardsWon(play.yardsNet) || TABLE_NULL;
    case 'enum':
      return formatEnum(play[column.key]);
    case 'bool':
      return formatBool(play[column.key]);
    case 'offPers':
      return formatOffPers(play.offensivePersonnel);
    case 'defPers':
      return formatDefPers(play.defensivePersonnel);
    case 'penalties':
      return formatPenalties(play.penalties);
    case 'decimal':
      return formatDecimal(play[column.key]);
    case 'team':
      return formatTeam(
        column.key === 'offense' ? play.offenseTeam : play.defenseTeam,
      );
    default:
      // synthetic/derived plain columns
      if (column.key === 'driveGameIndex')
        return formatPlain(play.drive?.gameIndex);
      return formatPlain(play[column.key]);
  }
};

// Drawn Penalty column value: the resolved player name(s); "Unknown" when the
// play is flagged as having a drawn penalty but no name could be resolved
// (including when the penalties query failed); null (renders as an em-dash)
// when no penalty was drawn.
export const DRAWN_PENALTY_UNKNOWN = 'Unknown';
export const resolveDrawnPenaltyPlayer = (play, playerNames) => {
  if (playerNames) return playerNames;
  return play?.drawnPenalty === true ? DRAWN_PENALTY_UNKNOWN : null;
};

export const buildPlaysGridRows = (plays) => {
  if (!Array.isArray(plays) || plays.length === 0) return [];
  return plays.map((play) =>
    PLAYS_GRID_COLUMNS.reduce(
      (row, column) => ({
        ...row,
        [column.key]: { ...column, label: cellLabel(play, column) },
      }),
      { id: play.id, checked: false },
    ),
  );
};

// Columns whose sort value is not a plain field path derive a comparable value
// here. Personnel sort by their formatted group string (so identical packages
// cluster together); penalties sort by how many were called on the play. The
// display-mapped text columns (play type, formation, coverage) sort by the same
// label the cell shows so ordering matches the screen - the raw enum/API value
// can order differently (e.g. TWO_POINT_CONVERSION displays as "2Point Attempt",
// RUSH as "Run"). Missing values return null so they still trail.
const SORT_ACCESSORS = {
  offensivePersonnel: (p) =>
    p.offensivePersonnel ? formatOffPers(p.offensivePersonnel) : null,
  defensivePersonnel: (p) =>
    p.defensivePersonnel ? formatDefPers(p.defensivePersonnel) : null,
  penalties: (p) => (Array.isArray(p.penalties) ? p.penalties.length : null),
  type: (p) => (p.type ? (API_PLAY_TYPES[p.type] ?? null) : null),
  formationGroup: (p) =>
    p.formationGroup ? formatEnum(p.formationGroup) : null,
  defensiveCoverage: (p) =>
    p.defensiveCoverage ? formatEnum(p.defensiveCoverage) : null,
};

// Sorts raw plays by the column's accessor, sortField or key, pushing plays
// with a null/undefined value to the end. Falls back to gameIndex ascending
// when the column is unknown, unsortable, or direction is DEFAULT.
export const sortPlaysGrid = (plays, sortKey, sortDirection) => {
  if (isEmpty(plays)) return [];
  const column = PLAYS_GRID_COLUMNS.find((c) => c.key === sortKey);
  if (
    !column ||
    !column.isSortable ||
    sortDirection === SORT_DIRECTIONS.DEFAULT
  ) {
    return orderBy(plays, ['gameIndex'], [SORT_DIRECTIONS.ASCENDING]);
  }
  // A column either provides a derived accessor (personnel/penalties) or reads
  // a field path - `sortField` may be nested (e.g. 'drive.gameIndex' or
  // 'offenseTeam.shortName'), so resolve it with lodash get.
  const valueOf =
    SORT_ACCESSORS[column.key] ||
    ((p) => get(p, column.sortField || column.key));
  // Treat empty strings as missing too (matches formatPlain rendering them as
  // TABLE_NULL), so a blank value sorts last rather than ahead of real values.
  const [withValue, withoutValue] = partition(
    plays,
    (p) => !isBlank(valueOf(p)),
  );
  const sorted = orderBy(withValue, [valueOf], [sortDirection]);
  const trailing = orderBy(
    withoutValue,
    ['gameIndex'],
    [SORT_DIRECTIONS.ASCENDING],
  );
  return sorted.concat(trailing);
};
